import Vue from 'vue';
import { cloneDeep } from 'lodash';
import { i18n } from '@/plugins/i18n';
import { decodeHTML, getRandomColor } from '@/utils/main';
import { getRelativeRange, VHUB_TOTAL_NUMBER_OF_PERIODS } from '@/utils/vhub-utils';
import { VitalityCategory } from '@/utils/classes/VitalityCategory';
import { VitalityLoadingState } from '@/utils/classes/VitalityLoadingState';

const VLS = VitalityLoadingState;

export default {

  /** Is any vitality-hub data loading */
  loading(state) {
    if (state.finalizingLoading === VLS.WORKING) {
      return true;
    }
    for (let i = 0, len = VitalityCategory.all.length; i < len; i++) {
      const loadingState = state.loading[VitalityCategory.all[i].key];
      if (loadingState && (loadingState.loading === VLS.FETCHING || loadingState.loading === VLS.WORKING)) {
        return true;
      }
    }
    return false;
  },

  /** Boolean: is all the data ready */
  allReady(state) {
    for (let i = 0, len = VitalityCategory.all.length; i < len; i++) {
      const loadingState = state.loading[VitalityCategory.all[i].key];
      if (!loadingState || loadingState.loading !== VLS.READY) {
        return false;
      }
    }
    return true;
  },

  /** Did data loading encounter any errors */
  dataHasErrors(state) {
    if (state.finalizingLoading === VLS.ERROR) {
      return true;
    }
    for (let i = 0, len = VitalityCategory.all.length; i < len; i++) {
      const loadingState = state.loading[VitalityCategory.all[i].key];
      if (loadingState && loadingState.loading === VLS.ERROR) {
        return true;
      }
    }
    return false;
  },

  /** Object: Param to send through XHR */
  rangeParam(state) {
    const range = state.ranges[0];
    const { start } = getRelativeRange(range, -VHUB_TOTAL_NUMBER_OF_PERIODS);
    return {
      start: start.format('YYYY-MM-DD'),
      end: range.end.format('YYYY-MM-DD'),
    };
  },

  /**
   * Array: All criteria for every team
   * @return {Array} produces an array of objects like {
   *    team: <team_obj>,
   *    scoreData: <score_data_of_criterion>
   *    ... all criterion keys (ley, weight, text(), etc.)
   *  }
   */
  allScoreItems(state, getters) {
    const items = [];
    if (!getters.allReady) {
      return items;
    }
    state.teamGroups.forEach((group) => {
      VitalityCategory.all.forEach((cat) => {
        cat.criteria.forEach((crit) => {
          const scoreData = group[crit.scoresKey][0];
          // Criterions 'score' function can return a parameter to prevent showing in tops and flops
          if (scoreData && !scoreData.data.noTopsFlops) {
            items.push({
              group,
              ...crit,
              scoreData,
              categoryKey: cat.key,
            });
          }
        });
      });
    });
    return items;
  },

  /**
   * Team ids of the current vitality groups
   * @return {Array} - Array of numbers
   */
  currentGroupsTeamIds(state) {
    return state.teamGroups.reduce(
      (ids, group) => [...ids, ...group.teams.map(t => t.id).filter(id => !ids.includes(id))],
      [],
    );
  },

  /**
   * Team ids of the current vitality groups that need an update
   * @return {Array} - Array of numbers
   */
  updateNeededTeamIds(state, getters) {
    return getters.updateNeededGroups.reduce(
      (ids, group) => [...ids, ...group.teams.map(t => t.id).filter(id => !ids.includes(id))],
      [],
    );
  },

  /** Groups where 'needsUpdate' param is true */
  updateNeededGroups(state) {
    return state.teamGroups.filter(g => g.needsUpdate);
  },

  rangeIsMonth(state) {
    if (state.ranges && state.ranges.length) {
      const { start, end } = state.ranges[0];
      return start.isSame(start.clone().startOf('month'), 'd') && end.isSame(end.clone().endOf('month'), 'd');
    }
    return false;
  },
};
